<script setup lang="ts">
import { helpers, maxLength, required } from '@vuelidate/validators';

import useVuelidate from '@vuelidate/core';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useDebounce } from '@vueuse/core';
import { defaultRegisterStateBuilder } from '../../../utils/stateBuilder';
import { email as emailValidator } from '../../../utils/validators';
import { MAX_EMAIL_LENGTH } from '../../../utils/constants/validationLimits';
import { track } from '../../../utils/tracking';

import FormElement from '../../molecules/form/FormElement.vue';
import FormInput from '../../atoms/form/FormInput.vue';
import FormLabel from '../../atoms/form/FormLabel.vue';
import FormMessage from '../../atoms/form/FormMessage.vue';
import { useProfile } from '../../../stores/profile-store';
import { useApplicationStore } from '../../../stores/application-store';
import { usePayloadStore } from '../../../stores/payload-store';
import { isEmailAvailable } from '../../../services/user';
import AppInfoBox from '../../molecules/app/AppInfoBox.vue';
import AppLink from '../../atoms/app/AppLink.vue';
import { useAuth } from '../../../stores/auth-store';
import WebOnly from '../../webview/WebOnly.vue';
import WebViewOnly from '../../webview/WebViewOnly.vue';
import { useInjectedErrors } from "../../../composables/use-injected-errors";

const props = withDefaults(defineProps<{
    debounceTime?: number;
}>(), {
    debounceTime: 250,
});

const profile = useProfile();
const auth = useAuth();
const route = useRoute();
const payload = usePayloadStore();
const application = useApplicationStore();
const { email: rawEmail, extractedEmail } = storeToRefs(application);
const email = useDebounce(rawEmail, props.debounceTime);
const { mailErrors } = useInjectedErrors();

const isEmailChangeTracked = ref(false);

// Track changed field after extraction only once
const trackEmailChange = computed(() => !isEmailChangeTracked.value &&
    extractedEmail.value &&
    extractedEmail.value !== rawEmail.value);

const shouldValidateMailAvailable = computed(() => profile.email !== rawEmail.value);
const mailAlreadyExists = computed(() => mailErrors.value.keys.includes('mail-already-exists'));

const emailAvailableValidator = helpers.withAsync(
    async (mail: string) => {
        if (mailAlreadyExists.value) {
            return false;
        }

        if (shouldValidateMailAvailable.value) {
            return isEmailAvailable(mail);
        }

        return true;
    },
);

const validations = {
    email: {
        required,
        email: emailValidator,
        maxLength: maxLength(MAX_EMAIL_LENGTH),
        available: emailAvailableValidator,
    },
};

const v$ = useVuelidate(validations, { email });

const showEmailAlreadyInUseBox = computed(() => v$.value.email.available.$invalid);

function blur() {
    v$.value.email.$touch();
    application.updateCvRenderData();

    if (trackEmailChange.value) {
        track({
            element: 'AP_F: field changed after extraction',
            elementDetail: 'email',
        });

        isEmailChangeTracked.value = true;
    }
}

const router = useRouter();
// Send logged-out users to login, log logged-in users out, send them to log in and redirect to form after.
const alreadyExistsPromptHref = computed(() => {
    if (!auth.isLoggedIn) {
        return router.resolve({
            name: 'login',
        });
    }

    return router.resolve({
        name: 'logout',
        query: {
            redirectUrl: new URL(router.resolve({
                name: 'login',
                query: {
                    redirectUrl: new URL(route.fullPath, window.location.origin).href,
                    uuid: payload.atsUserUuid,
                    payload: payload.encryptedPayload,
                    registerState:
                        defaultRegisterStateBuilder(payload.atsUserUuid, payload.encryptedPayload),
                },
            }).href, window.location.origin).href,
        },
    });
});
</script>

<template>
    <FormElement>
        <template #start>
            <FormLabel
                ref="emailLabel"
            >
                E-Mail-Adresse
            </FormLabel>
        </template>
        <FormInput
            v-model.trim="rawEmail"
            type="text"
            placeholder="E-Mail-Adresse"
            data-qa="email"
            data-gtm-element="AP_F: Personal Data"
            data-gtm-element-detail="Email-Address"
            :status="v$.email.$error && !v$.email.$pending ? 'error' : ''"
            :show-sparkle-icon="extractedEmail !== ''"
            @blur="blur"
            @update:model-value="mailErrors.clear()"
        />
        <template #end>
            <AppInfoBox
                v-if="showEmailAlreadyInUseBox"
                v-track-visibility="{
                    element: 'AP_F: Validation-Error',
                    elementDetail: 'existing email logged' + (auth.isLoggedIn ? '-in' : '-out'),
                }"
                data-qa="mail already exists info box"
                error
            >
                Die eingegebene E-Mail-Adresse ist mit einem registrierten Account verknüpft.
                <WebOnly>
                    <AppLink
                        :to="alreadyExistsPromptHref"
                        neutral
                        underline
                        data-gtm-element="AP_F: Mail Validation Box Link"
                    >
                        Bitte melde dich mit dem Account an<!-- avoid whitespace -->
                    </AppLink>, um fortzufahren, oder verwende eine andere E-Mail-Adresse.
                </WebOnly>
                <WebViewOnly>
                    Bitte schließe das Bewerbungsformular und melde dich mit dem Account an,
                    um fortzufahren, oder verwende eine andere E-Mail-Adresse.
                </WebViewOnly>
            </AppInfoBox>
            <FormMessage
                v-if="v$.email.$error && !v$.email.$pending"
                type="error"
                data-qa="email form message"
            >
                <template v-if="v$.email.maxLength.$invalid">
                    Bitte gib eine korrekte E-Mail-Adresse ein, mit max. {{ MAX_EMAIL_LENGTH }} Zeichen.
                </template>
                <template v-else-if="v$.email.required.$invalid || v$.email.email.$invalid">
                    Bitte gib eine korrekte E-Mail-Adresse ein.
                </template>
            </FormMessage>
        </template>
    </FormElement>
</template>
